import axios, { AxiosError, AxiosResponse } from 'axios';
import { getToken, removeToken, isTokenExpired } from '@/features/auth/utils/auth';
import { env } from './env';

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});

// Request interceptor to automatically add JWT token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    
    // Add token to Authorization header if it exists and is valid
    if (token && !isTokenExpired()) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.log(`⚠️ No token for request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Return successful response as is
    return response;
  },
  (error: AxiosError) => {
    const { response } = error;
    
    // Handle authentication errors
    if (response?.status === 401) {
      console.warn('Authentication error: Token expired or invalid');
      
      // Remove invalid token from storage
      removeToken();
      
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/auth';
      }
      
      return Promise.reject(new Error('Authentication failed. Please login again.'));
    }
    
    // Handle forbidden errors (insufficient permissions)
    if (response?.status === 403) {
      console.warn('Forbidden: Insufficient permissions');
      
      if (typeof window !== 'undefined') {
        window.location.href = '/unauthorized';
      }
      
      return Promise.reject(new Error('Insufficient permissions to access this resource.'));
    }
    
    // Handle server errors
    if (response?.status >= 500) {
      console.error('Server error:', response.status, response.data);
      return Promise.reject(new Error('Server error. Please try again later.'));
    }
    
    // Handle network errors
    if (!response) {
      console.error('Network error:', error.message);
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }
    
    // Handle other errors
    console.error('API error:', response.status, response.data);
    return Promise.reject(error);
  }
);

// Export axios instance for use throughout the application
export default api;
